import React, { useState, useEffect } from 'react';
import { useAvevaService } from '@services/useAvevaService';
import AvevaStreams from './AvevaStreams';
import AvevaDashboard from './AvevaDashboard';
import AvevaStreamDetails from './AvevaStreamDetails';
import AvevaAlerts from './AvevaAlerts';
import styles from './AvevaConnect.module.css';

type ConnectionStatus = 'connected' | 'disconnected' | 'error' | 'unknown';

const AvevaConnect = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'streams' | 'alerts'>('dashboard');
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('unknown');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStream, setSelectedStream] = useState<string | null>(null);
  const [authStatus, setAuthStatus] = useState<'checking' | 'authenticated' | 'unauthenticated'>(
    'checking'
  );

  // Use the MSAL-integrated service
  const avevaApi = useAvevaService();

  useEffect(() => {
    checkAuthAndConnection();
  }, []);

  const checkAuthAndConnection = async () => {
    setLoading(true);
    setError(null);

    try {
      // Check authentication status
      const isAuthenticated = avevaApi.isAuthenticated();
      setAuthStatus(isAuthenticated ? 'authenticated' : 'unauthenticated');

      // Test AVEVA connection using public endpoint (works without auth)
      const response = await avevaApi.checkHealth();

      if (response.data.connection?.success) {
        setConnectionStatus('connected');
      } else {
        setConnectionStatus('disconnected');
        setError(response.data.connection?.error || 'Failed to connect to AVEVA');
      }
    } catch (err: any) {
      setConnectionStatus('error');
      setError(err.message || 'Failed to connect to AVEVA');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    try {
      const success = await avevaApi.ensureAuthenticated();
      if (success) {
        setAuthStatus('authenticated');
        // After login, check connection again
        checkAuthAndConnection();
      }
    } catch (error: any) {
      setError('Login failed: ' + error.message);
    }
  };

  const renderContent = () => {
    if (selectedStream) {
      return (
        <AvevaStreamDetails streamId={selectedStream} onBack={() => setSelectedStream(null)} />
      );
    }

    switch (activeTab) {
      case 'dashboard':
        return <AvevaDashboard onSelectStream={setSelectedStream} />;
      case 'streams':
        return <AvevaStreams onSelectStream={setSelectedStream} />;
      case 'alerts':
        return <AvevaAlerts />;
      default:
        return <AvevaDashboard onSelectStream={setSelectedStream} />;
    }
  };

  const getStatusColor = (): string => {
    switch (connectionStatus) {
      case 'connected':
        return '#f4d03f';
      case 'disconnected':
      case 'error':
        return '#03235A';
      default:
        return '#666';
    }
  };

  const getStatusText = (): string => {
    switch (connectionStatus) {
      case 'connected':
        return 'Connected';
      case 'disconnected':
        return 'Disconnected';
      case 'error':
        return 'Error';
      default:
        return 'Unknown';
    }
  };

  if (loading && authStatus === 'checking') {
    return (
      <div className={styles.avevaConnect}>
        <div className={styles.loadingContainer} data-testid="aveva-loading">
          <div className={styles.spinner}>
          </div>
          <p>Checking authentication and AVEVA connection...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={styles.avevaConnect}
      data-testid="aveva-connect"
      data-connection-status={connectionStatus}
      data-auth-status={authStatus}
    >
      <header className={styles.avevaHeader}>
        <div className={styles.headerContent}>

          <h1>AVEVA Connect</h1>
          <div className={styles.connectionStatus}>
            <div className={styles.statusIndicator} style={{ backgroundColor: getStatusColor() }}></div>
            <span data-testid="aveva-connection-status" data-status={connectionStatus}>{getStatusText()}</span>
            <button className={styles.refreshBtn} onClick={checkAuthAndConnection} disabled={loading} data-testid="aveva-refresh-button">
              ↻
            </button>
          </div>
        </div>

        {!selectedStream && (
          <nav className={styles.avevaNav}>
            <button
              className={`${styles.navBtn} ${activeTab === 'dashboard' ? styles.navBtnActive : ''}`}
              onClick={() => setActiveTab('dashboard')}
              data-testid="aveva-tab-button"
              data-tab="dashboard"
              data-active={String(activeTab === 'dashboard')}
            >
              Dashboard
            </button>
            <button
              className={`${styles.navBtn} ${activeTab === 'streams' ? styles.navBtnActive : ''}`}
              onClick={() => setActiveTab('streams')}
              data-testid="aveva-tab-button"
              data-tab="streams"
              data-active={String(activeTab === 'streams')}
            >
              Streams
            </button>
            <button
              className={`${styles.navBtn} ${activeTab === 'alerts' ? styles.navBtnActive : ''}`}
              onClick={() => setActiveTab('alerts')}
              data-testid="aveva-tab-button"
              data-tab="alerts"
              data-active={String(activeTab === 'alerts')}
            >
              Alerts
            </button>
          </nav>
        )}
      </header>

      {error && (
        <div className={styles.errorBanner} data-testid="aveva-error-banner">
          <span>⚠ Failed to connect to Aveva</span>
          <button onClick={() => setError(null)} data-testid="aveva-error-dismiss-button">✕</button>
        </div>
      )}

      <main className={styles.avevaContent}>
        {authStatus === 'unauthenticated' ? (
          <div className={styles.authRequired} data-testid="aveva-auth-required">
            <h2 className={styles.authTitle}>
              Authentication Required for Protected Features
            </h2>

            <p className={styles.authText}>
              Some AVEVA features require authentication. You can view basic connection status
              without logging in.
            </p>

            <button className={styles.loginBtn} onClick={handleLogin} data-testid="aveva-login-button">
              Log In for Full Access
            </button>
          </div>
        ) : (
          renderContent()
        )}
      </main>
    </div>
  );
};

export default AvevaConnect;
